import React, { useState, useEffect, useCallback } from 'react';
import { startOfMonth, endOfMonth, eachDayOfInterval, format, addMonths, subMonths, parseISO } from 'date-fns';
import es from 'date-fns/locale/es';
import Modal from 'react-modal';
import { getDevOpsMembers, getTeamEvents, createTeamEvent, updateTeamEvent, deleteTeamEvent } from './api';
import { isHoliday, isSaturday, isSunday } from './holidays';
import './CalendarioEquipo.css';

// Tipos de eventos predefinidos con colores
const EVENT_TYPES = [
  { value: 'Vacaciones (Tentativa)', label: 'Vacaciones (Tentativa)', color: '#81C784', adminOnly: false },
  { value: 'Vacaciones (Aprobadas)', label: 'Vacaciones (Aprobadas)', color: '#4CAF50', adminOnly: true },
  { value: 'N1', label: 'N1', color: '#2196F3', adminOnly: true },
  { value: 'N2', label: 'N2', color: '#FF9800', adminOnly: true },
  { value: 'Baja', label: 'Baja Médica', color: '#F44336', adminOnly: true },
];

function CalendarioEquipo() {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamEvents, setTeamEvents] = useState([]);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [formData, setFormData] = useState({
    event_type: 'Vacaciones (Tentativa)',
    start_date: '',
    end_date: ''
  });

  const loadTeamEvents = useCallback(async () => {
    try {
      const monthStart = startOfMonth(currentMonth);
      const monthEnd = endOfMonth(currentMonth);
      const response = await getTeamEvents(
        format(monthStart, 'yyyy-MM-dd'),
        format(monthEnd, 'yyyy-MM-dd')
      );
      setTeamEvents(response.data);
    } catch (error) {
      console.error('Error loading team events:', error);
    }
  }, [currentMonth]);

  useEffect(() => {
    loadCurrentUser();
    loadTeamMembers();
  }, []);

  useEffect(() => {
    loadTeamEvents();
  }, [currentMonth, loadTeamEvents]);

  const loadCurrentUser = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const response = await fetch('/api/auth/me', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const user = await response.json();
        setCurrentUser(user);
      }
    } catch (error) {
      console.error('Error loading user:', error);
    }
  };

  const loadTeamMembers = async () => {
    try {
      const response = await getDevOpsMembers();
      setTeamMembers(response.data);
    } catch (error) {
      console.error('Error loading team members:', error);
    }
  };

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  const previousMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
  const nextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));
  const goToToday = () => setCurrentMonth(new Date());

  const getDayClassName = (day) => {
    if (isHoliday(day)) return 'day-holiday';
    if (isSunday(day)) return 'day-sunday';
    if (isSaturday(day)) return 'day-saturday';
    return '';
  };

  const getEventsForCell = (memberEmail, day) => {
    return teamEvents.filter(event => {
      if (event.member_email !== memberEmail) return false;
      const eventStart = parseISO(event.start_date);
      const eventEnd = parseISO(event.end_date);

      // Normalizar las fechas a medianoche para comparación correcta
      const dayNormalized = new Date(day.getFullYear(), day.getMonth(), day.getDate());
      const startNormalized = new Date(eventStart.getFullYear(), eventStart.getMonth(), eventStart.getDate());
      const endNormalized = new Date(eventEnd.getFullYear(), eventEnd.getMonth(), eventEnd.getDate());

      return dayNormalized >= startNormalized && dayNormalized <= endNormalized;
    });
  };

  const openModal = (member, day, existingEvent = null) => {
    // Verificar si el usuario puede editar este evento
    const isAdmin = currentUser?.role === 'admin';
    const isOwnEvent = member.email === currentUser?.email;

    // Si el evento existe y no es admin ni el dueño, no permitir edición
    if (existingEvent && !isAdmin && !isOwnEvent) {
      alert('Solo puedes editar tus propios eventos');
      return;
    }

    setSelectedMember(member);
    setSelectedEvent(existingEvent);

    if (existingEvent) {
      setFormData({
        event_type: existingEvent.event_type,
        start_date: existingEvent.start_date,
        end_date: existingEvent.end_date
      });
    } else {
      setFormData({
        event_type: 'Vacaciones (Tentativa)',
        start_date: format(day, 'yyyy-MM-dd'),
        end_date: format(day, 'yyyy-MM-dd')
      });
    }

    setModalIsOpen(true);
  };

  const closeModal = () => {
    setModalIsOpen(false);
    setSelectedMember(null);
    setSelectedEvent(null);
    setFormData({
      event_type: 'Vacaciones (Tentativa)',
      start_date: '',
      end_date: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const eventData = {
        member_email: selectedMember.email,
        ...formData
      };

      if (selectedEvent) {
        await updateTeamEvent(selectedEvent.id, eventData);
      } else {
        await createTeamEvent(eventData);
      }

      loadTeamEvents();
      closeModal();
    } catch (error) {
      console.error('Error saving team event:', error);
      alert('Error al guardar el evento');
    }
  };

  const handleDelete = async () => {
    if (selectedEvent && window.confirm('¿Estás seguro de eliminar este evento?')) {
      try {
        await deleteTeamEvent(selectedEvent.id);
        loadTeamEvents();
        closeModal();
      } catch (error) {
        console.error('Error deleting team event:', error);
        alert('Error al eliminar el evento');
      }
    }
  };

  const getEventTypeColor = (eventType) => {
    const type = EVENT_TYPES.find(t => t.value === eventType);
    return type ? type.color : '#999';
  };

  const getEventTypeShortLabel = (eventType) => {
    const shortLabels = {
      'Vacaciones (Tentativa)': 'Vac (T)',
      'Vacaciones (Aprobadas)': 'Vac (A)',
      'N1': 'N1',
      'N2': 'N2',
      'Baja': 'Baja'
    };
    return shortLabels[eventType] || eventType;
  };

  const getAvailableEventTypes = () => {
    const isAdmin = currentUser?.role === 'admin';
    const isOwnEvent = selectedMember?.email === currentUser?.email;

    if (isAdmin) {
      return EVENT_TYPES; // Admin puede ver todos los tipos
    }

    // Usuario normal solo puede crear eventos en su propia fila
    if (isOwnEvent) {
      return EVENT_TYPES.filter(type => !type.adminOnly);
    }

    return [];
  };

  const canEditEvent = () => {
    const isAdmin = currentUser?.role === 'admin';
    const isOwnEvent = selectedMember?.email === currentUser?.email;
    return isAdmin || isOwnEvent;
  };

  return (
    <div className="calendario-equipo">
      <div className="calendar-header">
        <button onClick={previousMonth} className="nav-button">← Anterior</button>
        <div className="month-display">
          <h2>{format(currentMonth, 'MMMM yyyy', { locale: es })}</h2>
          <button onClick={goToToday} className="today-button">Hoy</button>
        </div>
        <button onClick={nextMonth} className="nav-button">Siguiente →</button>
      </div>

      <div className="calendar-grid-container">
        <table className="calendar-table">
          <thead>
            <tr>
              <th className="member-column">Miembro</th>
              {daysInMonth.map(day => (
                <th key={day.toISOString()} className={`day-header ${getDayClassName(day)}`}>
                  <div className="day-number">{format(day, 'd')}</div>
                  <div className="day-name">{format(day, 'EEE', { locale: es })}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {teamMembers.map(member => (
              <tr key={member.email}>
                <td className="member-cell">
                  <div className="member-name">{member.name}</div>
                </td>
                {daysInMonth.map(day => {
                  const events = getEventsForCell(member.email, day);
                  return (
                    <td
                      key={`${member.email}-${day.toISOString()}`}
                      className={`day-cell ${getDayClassName(day)} ${events.length > 0 ? 'has-events' : ''}`}
                      onClick={() => openModal(member, day, events[0])}
                    >
                      <div className="cell-content">
                        {events.map(event => (
                          <div
                            key={event.id}
                            className="event-indicator"
                            style={{ backgroundColor: getEventTypeColor(event.event_type) }}
                            title={event.event_type}
                          >
                            {getEventTypeShortLabel(event.event_type)}
                          </div>
                        ))}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="legend">
        <div className="legend-section">
          <h4>Días Especiales:</h4>
          <div className="legend-items">
            <div className="legend-item">
              <div className="legend-box day-saturday"></div>
              <span>Sábado</span>
            </div>
            <div className="legend-item">
              <div className="legend-box day-sunday"></div>
              <span>Domingo</span>
            </div>
            <div className="legend-item">
              <div className="legend-box day-holiday"></div>
              <span>Festivo</span>
            </div>
          </div>
        </div>
        <div className="legend-section">
          <h4>Tipos de Evento:</h4>
          <div className="legend-items">
            {EVENT_TYPES.map(type => (
              <div key={type.value} className="legend-item">
                <div className="legend-box" style={{ backgroundColor: type.color }}></div>
                <span>{type.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        contentLabel="Team Event Modal"
        className="modal"
        overlayClassName="modal-overlay"
      >
        <h2>{selectedEvent ? 'Editar Evento' : 'Nuevo Evento'}</h2>
        {selectedMember && (
          <p><strong>Miembro:</strong> {selectedMember.name}</p>
        )}
        {!canEditEvent() && (
          <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
            <span className="alert-icon">⚠️</span>
            Solo puedes crear/editar eventos en tu propia fila
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Tipo de Evento *</label>
            <select
              value={formData.event_type}
              onChange={(e) => setFormData({ ...formData, event_type: e.target.value })}
              required
              disabled={!canEditEvent()}
            >
              {getAvailableEventTypes().map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            {currentUser?.role !== 'admin' && (
              <small style={{ color: '#666', display: 'block', marginTop: '0.5rem' }}>
                Los eventos N1, N2, Baja Médica y Vacaciones (Aprobadas) solo pueden ser asignados por administradores
              </small>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Fecha Inicio *</label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>Fecha Fin *</label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                required
              />
            </div>
          </div>

          <div className="modal-buttons">
            <button type="submit" className="btn-primary" disabled={!canEditEvent()}>
              {selectedEvent ? 'Actualizar' : 'Crear'}
            </button>
            {selectedEvent && canEditEvent() && (
              <button type="button" onClick={handleDelete} className="btn-danger">
                Eliminar
              </button>
            )}
            <button type="button" onClick={closeModal} className="btn-secondary">
              Cancelar
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default CalendarioEquipo;
